import { faCircleDot } from "@fortawesome/free-regular-svg-icons/faCircleDot";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faHashtag } from "@fortawesome/free-solid-svg-icons/faHashtag";
import { faListCheck } from "@fortawesome/free-solid-svg-icons/faListCheck";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faPuzzlePiece } from "@fortawesome/free-solid-svg-icons/faPuzzlePiece";
import { faTextHeight } from "@fortawesome/free-solid-svg-icons/faTextHeight";
import { faTextWidth } from "@fortawesome/free-solid-svg-icons/faTextWidth";
import omit from "lodash/omit";
import React, { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { FontSize, Text } from "../../../../components/Text";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { InputTypes } from "../../../../model/form/types/InputTypes.enum";
import { ModuleDefinitionKind } from "../../../../model/module/types/module-definition/ModuleDefinitionKind";
import { ModuleInputCreateEdit } from "../../../../model/module/types/module-definition/ModuleInputCreateEdit";
import { ModuleDefinitionTemplates } from "../../../../model/module/types/module-definition/ModuleDefinitionTemplates";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { getModuleTemplateInputs, moduleTemplates } from "../utils/moduleUtils";
import { TestIDs } from "../../../../tests/types/testIds";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xl};
  width: 100%;
  max-width: 68rem;
  align-self: flex-start;
`;

const OptionalSpan = styled.span`
  font-size: ${FontSize.m};
  font-style: italic;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${Spacing.m};
  width: 100%;

  @media (max-width: ${Breakpoints.DESKTOP}) {
    grid-template-columns: 1fr;
  }
`;

const TemplateCard = styled(Clickable)<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.l};
  border-radius: ${Rounded.xl};
  border: 2px solid
    ${({ $selected, theme }) => ($selected ? theme.main : theme.darkSub)};
  background: ${({ theme }) =>
    `linear-gradient(180deg, ${theme.white} 0%, ${theme.sub} 100%)`};
  box-shadow: ${({ $selected }) => ($selected ? Shadow.MD : Shadow.SM)};
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  min-height: 18rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadow.MD};
    border-color: ${({ theme }) => theme.main};
  }
`;

const CardHeader = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  gap: ${Spacing.m};
`;

const StatusBadge = styled(Flex)<{ $selected: boolean }>`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.main : theme.sub};
  color: ${({ $selected, theme }) =>
    $selected ? theme.white : theme.darkerSub};
  font-size: ${FontSize.xs};
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const PreviewPanel = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border-radius: ${Rounded.lg};
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.darkSub};
`;

const PreviewRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.sub};
  border: 1px solid ${({ theme }) => theme.darkSub};
`;

const PreviewRowLeft = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  min-width: 0;
`;

const TypeIconBubble = styled(Flex)`
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => `${theme.main}12`};
  color: ${({ theme }) => theme.main};
`;

const FieldMetaPill = styled(Flex)<{ $required?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ $required, theme }) =>
    $required ? `${theme.main}14` : theme.sub};
  color: ${({ $required, theme }) =>
    $required ? theme.main : theme.darkerSub};
  font-size: ${FontSize.xs};
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const SummaryRow = styled(Flex)`
  gap: ${Spacing.xs};
  flex-wrap: wrap;
`;

const SummaryPill = styled(Flex)`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.textColor};
  font-size: ${FontSize.xs};
  line-height: 1.1;
`;

const getAllowedModuleTemplates = (moduleKind?: ModuleDefinitionKind) => {
  return moduleKind === ModuleDefinitionKind.DYNAMIC
    ? moduleTemplates
    : (omit(moduleTemplates, "NOTE") as typeof moduleTemplates);
};

const getInputTypeIcon = (type: InputTypes) => {
  switch (type?.toLowerCase()) {
    case InputTypes.TEXTAREA:
      return faTextHeight;
    case InputTypes.DATE:
      return faCalendar;
    case InputTypes.EMAIL:
      return faEnvelope;
    case InputTypes.NUMBER:
      return faHashtag;
    case InputTypes.SELECT:
      return faListCheck;
    case InputTypes.RADIO:
      return faCircleDot;
    case InputTypes.PHONE:
      return faPhone;
    default:
      return faTextWidth;
  }
};

const getInputTypeLabel = (
  type: InputTypes,
  t: (key: string) => string,
): string => {
  if (type?.toLowerCase() === InputTypes.PHONE) {
    return t("common.phone");
  }

  return t(`common.${type.toLowerCase()}`);
};

export const ModuleDefinitionCreateEditTemplatesStep: React.FC = () => {
  const { t } = useTranslation();
  const { currentStep } = useStepContext();

  const { control, clearErrors, setValue, watch } = useFormContext<
    (CreateModuleDefinitionDto | UpdateModuleDefinitionDto) & {
      template?: ModuleDefinitionTemplates | null;
    }
  >();

  const [moduleKind] = watch(["kind"]);

  const allowedTemplates = useMemo(
    () => getAllowedModuleTemplates(moduleKind),
    [moduleKind],
  );

  const availableTemplates = useMemo(
    () => Object.keys(allowedTemplates) as ModuleDefinitionTemplates[],
    [allowedTemplates],
  );

  const canGoNext = useCallback(() => {
    return true;
  }, []);

  useStepperRegisterValidation(currentStep, canGoNext);

  const onSelectTemplate = (
    onChange: (value: ModuleDefinitionTemplates | null) => void,
    nextTemplate: ModuleDefinitionTemplates | null,
  ) => {
    onChange(nextTemplate);
    clearErrors("inputs");

    if (!nextTemplate) {
      return;
    }

    setValue(
      "inputs",
      getModuleTemplateInputs(nextTemplate) as ModuleInputCreateEdit[],
    );
  };

  return (
    <Container>
      <Controller
        name="template"
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex flexDirection="column" gap="m" width="100%">
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faPuzzlePiece} color="main" size="xl" />

              <Text fontSize="l" fontWeight="bold" color="main">
                {t("modules.definition.templates.title")}
                <OptionalSpan> ({t("common.optional")})</OptionalSpan>
              </Text>
            </Flex>

            <Text color="darkerSub">
              {t("modules.definition.templates.description")}
            </Text>

            <CardsGrid>
              {availableTemplates.map((templateKey) => {
                const templateInputs = allowedTemplates[templateKey] || [];
                const requiredCount = templateInputs.filter(
                  (input) => input.settings?.required,
                ).length;
                const optionalCount = templateInputs.length - requiredCount;
                const selected = value === templateKey;

                return (
                  <TemplateCard
                    key={templateKey}
                    $selected={selected}
                    onClick={() => onSelectTemplate(onChange, templateKey)}
                    dataTestId={TestIDs.modules.createEdit.stepTemplate.template(
                      templateKey,
                    )}
                  >
                    <CardHeader>
                      <Flex flexDirection="column" gap="xs">
                        <Text fontWeight="bold" fontSize="m">
                          {t(
                            `modules.definition.templates.template.${templateKey.toLowerCase()}.label`,
                          )}
                        </Text>
                        <Text color="darkerSub" fontSize="s">
                          {t(`modules.definition.templates.fields-ready`, {
                            count: templateInputs.length,
                          })}
                        </Text>
                      </Flex>

                      <StatusBadge $selected={selected}>
                        {selected ? t("common.selected") : t("common.choose")}
                      </StatusBadge>
                    </CardHeader>

                    <PreviewPanel>
                      {templateInputs.map((input) => (
                        <PreviewRow key={input.name}>
                          <PreviewRowLeft>
                            <TypeIconBubble>
                              <AtiraIcon
                                icon={getInputTypeIcon(input.type)}
                                color="main"
                              />
                            </TypeIconBubble>

                            <Flex flexDirection="column" gap="xs" minWidth="0">
                              <Text fontWeight="bold" fontSize="s" cutText>
                                {input.label}
                              </Text>
                              <Text fontSize="xs" color="darkerSub">
                                {getInputTypeLabel(input.type, t)}
                              </Text>
                            </Flex>
                          </PreviewRowLeft>

                          <FieldMetaPill $required={input.settings?.required}>
                            {input.settings?.required
                              ? t("common.required")
                              : t("common.optional")}
                          </FieldMetaPill>
                        </PreviewRow>
                      ))}
                    </PreviewPanel>

                    <SummaryRow>
                      <SummaryPill>
                        {requiredCount} {t("common.required")}
                      </SummaryPill>
                      <SummaryPill>
                        {optionalCount} {t("common.optional")}
                      </SummaryPill>
                    </SummaryRow>
                  </TemplateCard>
                );
              })}
            </CardsGrid>

            {error?.message ? (
              <Text color="danger" fontSize="s">
                {error.message}
              </Text>
            ) : null}
          </Flex>
        )}
      />
    </Container>
  );
};
